'use client';

// Framer Motion removed - using CSS transitions for better performance
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { getBrandColor } from '@/config/brand-colors';

interface PolicyPageContentProps {
  title: string;
  paragraphs: string[];
  lastUpdated: Date;
}

export default function PolicyPageContent({ title, paragraphs, lastUpdated }: PolicyPageContentProps) {
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(lastUpdated));

  // CSS-only animation state
  const [isVisible, setIsVisible] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Trigger animations on mount
    setIsVisible(true);
  }, []);

  return (
    <div 
      className="min-h-screen"
      style={{ 
        backgroundColor: '#ffffff',
        color: getBrandColor('black'),
      }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50 border-b"
        style={{ 
          backgroundColor: '#ffffff',
          borderColor: getBrandColor('primaryBlue') + '20',
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              href="/"
              className="flex items-center gap-2 text-sm font-medium transition-colors duration-200"
              style={{ color: getBrandColor('deepNavy') }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <article className="max-w-3xl mx-auto">
          {/* Title */}
          <div
            ref={titleRef}
            className="mb-8 md:mb-12"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
            }}
          >
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-4"
              style={{ color: getBrandColor('deepNavy') }}
            >
              {title}
            </h1>
            <p 
              className="text-sm"
              style={{ color: getBrandColor('black') + '80' }}
            >
              Last updated: {formattedDate}
            </p>
          </div>

          {/* Content */}
          <div
            ref={contentRef}
            className="space-y-6"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s',
            }}
          >
            {paragraphs.length > 0 ? (
              paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-base md:text-lg leading-relaxed"
                  style={{ 
                    color: getBrandColor('black') + 'E6',
                    textAlign: 'justify',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                    transition: `opacity 0.4s ease-out ${0.3 + (index * 0.05)}s, transform 0.4s ease-out ${0.3 + (index * 0.05)}s`,
                  }}
                >
                  {paragraph}
                </p>
              ))
            ) : (
              <p 
                className="text-base md:text-lg leading-relaxed italic"
                style={{ color: getBrandColor('black') + '80' }}
              >
                This policy is currently being updated. Please check back later.
              </p>
            )}
          </div>

          {/* Back to Top */}
          <div
            ref={footerRef}
            className="mt-12 md:mt-16 pt-8 border-t"
            style={{ 
              borderColor: getBrandColor('primaryBlue') + '20',
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.6s ease-out 0.6s',
            }}
          >
            <Link 
              href="/"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-200"
              style={{ color: getBrandColor('primaryBlue') }}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Return to Home</span>
            </Link>
          </div>
        </article>
      </main>

      {/* Simple Footer */}
      <footer 
        className="border-t py-8"
        style={{ 
          backgroundColor: '#ffffff',
          borderColor: getBrandColor('primaryBlue') + '20',
        }}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p 
            className="text-center text-sm"
            style={{ color: getBrandColor('black') + '80' }}
          >
            © {new Date().getFullYear()} e-voque. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

